import { useEffect, useState } from 'react';
import { useAuth } from '../api.js';
import { canGenerateSshKey, generateSshKeypair } from '../lib/sshkey.js';
import { useCarriers } from '../lib/useCarriers.js';
import { Button, Card, inputClass } from './ui.js';

/** Trigger a client-side file download (used for the generated private key). */
function downloadText(filename: string, text: string) {
  const url = URL.createObjectURL(new Blob([text], { type: 'application/octet-stream' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

interface Rule {
  pattern: string;
  carrierId: string;
}
interface Pickup {
  id: string;
  sourceKey: string;
  status: string;
  error: string | null;
}
interface Connection {
  configured: boolean;
  host: string | null;
  port: number;
  username: string;
  keySet?: boolean;
}

/**
 * SFTP drop-folder ingestion (enterprise, app-only). Lets the account configure how
 * dropped filenames map to a carrier + period, see the drop-folder path their SFTP
 * host should target, trigger an immediate scan, and review recent pickups. Gated by
 * the `sftp` entitlement — hidden entirely when off. Direct fetch (off the SDK).
 *
 * The SFTP host is an SFTPGo container (Cloudflare Container → R2); this card shows
 * the connection details, lets the customer set their SSH key, and configures how
 * dropped filenames map to a carrier + period.
 */
export function SftpSettings() {
  const { baseUrl, token, entitlements } = useAuth();
  const url = `${baseUrl.replace(/\/$/, '')}/sftp`;
  const headers = { authorization: `Bearer ${token}`, 'content-type': 'application/json' };

  const [inboxPrefix, setInboxPrefix] = useState('');
  const [rules, setRules] = useState<Rule[]>([]);
  const { carriers } = useCarriers();
  const [pickups, setPickups] = useState<Pickup[]>([]);
  const [connection, setConnection] = useState<Connection | null>(null);
  const [publicKey, setPublicKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  // Whether this browser can generate keys, and a one-time "downloaded" confirmation.
  const [canGen, setCanGen] = useState(false);
  const [generated, setGenerated] = useState(false);

  const loadConfig = () =>
    fetch(`${url}/config`, { headers })
      .then((r) => (r.ok ? r.json() : null))
      .then((cfg) => {
        if (!cfg) return;
        setInboxPrefix(cfg.inboxPrefix ?? '');
        setRules(Array.isArray(cfg.rules) ? cfg.rules : []);
        setConnection(cfg.connection ?? null);
      })
      .catch(() => {});
  const loadPickups = () =>
    fetch(`${url}/pickups`, { headers })
      .then((r) => (r.ok ? r.json() : { data: [] }))
      .then((b) => setPickups(b.data ?? []))
      .catch(() => {});

  // biome-ignore lint/correctness/useExhaustiveDependencies: load once on mount
  useEffect(() => {
    if (token && entitlements.sftp) {
      loadConfig();
      loadPickups();
      canGenerateSshKey().then(setCanGen);
    }
  }, [token]);

  // Generate a keypair IN THE BROWSER, download the private key (never sent to us),
  // and register only the public key with the server.
  const generateKey = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const { publicKey: pub, privateKey } = await generateSshKeypair(
        `commissionsight-${connection?.username ?? 'sftp'}`,
      );
      downloadText('commissionsight-sftp-key', privateKey); // the private key, for the user
      const res = await fetch(`${url}/key`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ publicKey: pub }),
      });
      if (!res.ok) {
        const b = await res.json().catch(() => null);
        throw new Error(b?.title ?? `Failed (${res.status})`);
      }
      setGenerated(true);
      setMsg('Key generated. Your private key was downloaded — save it now (we never receive it).');
      await loadConfig();
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Failed to generate key');
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const clean = rules.filter((r) => r.pattern.trim() && r.carrierId);
      const res = await fetch(`${url}/config`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ enabled: true, rules: clean }),
      });
      if (!res.ok) throw new Error('Save failed');
      setMsg('Saved.');
      await loadConfig();
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const scan = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const res = await fetch(`${url}/scan`, { method: 'POST', headers });
      const s = await res.json();
      setMsg(`Scan complete: ${s.enqueued} queued, ${s.failed} unprocessed, ${s.skipped} skipped.`);
      await loadPickups();
    } finally {
      setBusy(false);
    }
  };

  const saveKey = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const res = await fetch(`${url}/key`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ publicKey: publicKey.trim() }),
      });
      if (!res.ok) {
        const b = await res.json().catch(() => null);
        throw new Error(b?.title ?? `Failed (${res.status})`);
      }
      setPublicKey('');
      setMsg('SSH key saved — you can now connect.');
      await loadConfig();
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Failed to save key');
    } finally {
      setBusy(false);
    }
  };

  if (!entitlements.sftp) return null;

  return (
    <Card>
      <h2 className="font-semibold">SFTP ingestion</h2>
      <p className="mt-1 text-sm text-neutral-500">
        Drop statements into your SFTP folder and they're ingested automatically. Each rule maps a
        filename to a carrier; the period is read from the filename (use named groups{' '}
        <code>{'(?<year>\\d{4})'}</code> and <code>{'(?<month>\\d{2})'}</code>). A file whose month
        can't be determined is left unprocessed.
      </p>
      {connection?.configured ? (
        <div className="mt-3 space-y-3 rounded-lg border border-neutral-200 p-3 text-sm dark:border-neutral-700">
          <div className="grid gap-1 sm:grid-cols-3">
            <div>
              <div className="text-xs uppercase tracking-wide text-neutral-400">Host</div>
              <code className="font-mono">{connection.host ?? '—'}</code>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-neutral-400">Port</div>
              <code className="font-mono">{connection.port}</code>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-neutral-400">Username</div>
              <code className="font-mono">{connection.username}</code>
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-neutral-400">
              SSH key{' '}
              {connection.keySet
                ? '(set ✓ — generate or paste a new one to rotate)'
                : '(required to connect)'}
            </div>
            <p className="mt-1 text-sm text-neutral-500">
              SFTP uses key pairs, not passwords: a <strong>public</strong> key we store, and a{' '}
              <strong>private</strong> key only you hold. New to this? Let us generate the pair for
              you — we keep the public key and your private key downloads to your computer (we never
              see it).
            </p>

            {canGen && (
              <div className="mt-2">
                <Button size="sm" onClick={generateKey} loading={busy}>
                  ✨ Generate a key for me
                </Button>
                {generated && (
                  <div className="mt-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-700 dark:bg-amber-950/30 dark:text-amber-200">
                    <strong>Saved your private key as “commissionsight-sftp-key”.</strong> Keep it
                    safe — it’s shown once and is never sent to CommissionSight. Load it into your
                    SFTP client (WinSCP/FileZilla: it’s your “private key file”; command line:{' '}
                    <code>
                      sftp -i commissionsight-sftp-key {connection.username}@{connection.host}
                    </code>
                    ).
                  </div>
                )}
              </div>
            )}

            <details className="mt-3 text-sm">
              <summary className="cursor-pointer text-neutral-500">
                Prefer to make your own key? (ssh-keygen)
              </summary>
              <div className="mt-2 space-y-2 text-neutral-600 dark:text-neutral-300">
                <p>
                  On a Mac/Linux terminal or Windows PowerShell, run (press Enter at each prompt):
                </p>
                <pre className="overflow-x-auto rounded-md bg-neutral-900 p-2 font-mono text-xs text-neutral-100">
                  ssh-keygen -t ed25519 -f commissionsight-sftp -C "you@agency.com"
                </pre>
                <p>
                  That makes two files. Open the <code>.pub</code> one (
                  <code>commissionsight-sftp.pub</code>), copy its single line, and paste it below.
                  Keep the other file (no extension) private — that’s the key your SFTP client uses.
                </p>
                <textarea
                  className={`${inputClass} mt-1 h-20 font-mono text-xs`}
                  value={publicKey}
                  onChange={(e) => setPublicKey(e.target.value)}
                  placeholder="ssh-ed25519 AAAAC3Nz... you@agency.com"
                />
                <Button
                  size="sm"
                  onClick={saveKey}
                  loading={busy}
                  disabled={publicKey.trim().length < 20}
                >
                  Save pasted key
                </Button>
              </div>
            </details>
          </div>
        </div>
      ) : (
        <div className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:bg-amber-950/30 dark:text-amber-300">
          The SFTP gateway isn't configured for this environment yet. Rules below still apply once
          it's live; contact CommissionSight to finish SFTP setup.
        </div>
      )}

      <div className="mt-3 rounded-lg bg-neutral-50 px-3 py-2 text-sm dark:bg-neutral-800/50">
        Upload into folder: <code className="font-mono">{inboxPrefix || '—'}</code>
      </div>

      <div className="mt-4 space-y-2">
        {rules.map((rule, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: rows are positional + editable
          <div key={i} className="grid gap-2 md:grid-cols-[1fr_220px_auto]">
            <input
              className={inputClass}
              value={rule.pattern}
              placeholder="^aetna_(?<year>\d{4})-(?<month>\d{2})\.csv$"
              onChange={(e) =>
                setRules((rs) =>
                  rs.map((r, idx) => (idx === i ? { ...r, pattern: e.target.value } : r)),
                )
              }
            />
            <select
              className={inputClass}
              value={rule.carrierId}
              onChange={(e) =>
                setRules((rs) =>
                  rs.map((r, idx) => (idx === i ? { ...r, carrierId: e.target.value } : r)),
                )
              }
            >
              <option value="">Select carrier…</option>
              {carriers.map((carr) => (
                <option key={carr.id} value={carr.id}>
                  {carr.name}
                </option>
              ))}
            </select>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setRules((rs) => rs.filter((_, idx) => idx !== i))}
            >
              Remove
            </Button>
          </div>
        ))}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setRules((rs) => [...rs, { pattern: '', carrierId: '' }])}
        >
          + Add rule
        </Button>
      </div>

      {msg && <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-300">{msg}</p>}
      <div className="mt-3 flex gap-2">
        <Button onClick={save} loading={busy}>
          Save rules
        </Button>
        <Button variant="ghost" onClick={scan} loading={busy}>
          Scan inbox now
        </Button>
      </div>

      {pickups.length > 0 && (
        <div className="mt-4">
          <h3 className="text-sm font-medium">Recent pickups</h3>
          <ul className="mt-2 space-y-1 text-sm">
            {pickups.slice(0, 10).map((p) => (
              <li key={p.id} className="flex items-center justify-between gap-2">
                <span className="truncate font-mono text-xs text-neutral-500">{p.sourceKey}</span>
                <span
                  className={
                    p.status === 'enqueued'
                      ? 'text-emerald-600'
                      : p.status === 'failed'
                        ? 'text-rose-600'
                        : 'text-neutral-500'
                  }
                >
                  {p.status}
                  {p.error ? ` · ${p.error}` : ''}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}
